/**
 * 總覽 (/dashboard, boards/Main.dc.html) — read_only+. Roster size split by status and by
 * DERIVED origin (GET /api/whitelist/stats), the license pool (GET /api/licenses/stats), 30-day
 * redemptions, the to-do list and recent activity. Each block hides itself when its endpoint
 * answers 403 for this role (e.g. recent activity reads the admin-only audit log).
 */
import { Link } from 'react-router-dom';
import { SeriesChart } from '@/components/charts';
import { Swatch } from '@/components/RosterBits';
import { useAuth } from '@/contexts/AuthContext';
import { usePoll } from '@/lib/usePoll';
import { api, isApiError } from '@/lib/api';
import { roleAtLeast } from '@/lib/roles';
import { auditActor, auditSentence, isRoutineAudit } from '@/lib/auditText';
import { fmtDate, fmtRelative } from '@/lib/format';
import { ORIGIN_COLOR, ORIGIN_LABEL, ORIGIN_ORDER, pct } from '@/lib/labels';
import type { AnalyticsTimeline, AuditEntry, DashboardSummary, LicenseStats, RosterStats } from '@/types';

const forbidden = (e: unknown) => isApiError(e) && e.status === 403;
const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

function hhmm(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function DashboardHome() {
  const { role } = useAuth();
  const isAdmin = roleAtLeast(role, 'admin');

  const stats = usePoll<RosterStats>(() => api.rosterStats(), 15_000);
  const lic = usePoll<LicenseStats>(() => api.licenseStats(), 15_000);
  const timeline = usePoll<AnalyticsTimeline>(() => api.analyticsTimeline({ days: 30 }), 60_000);
  const summary = usePoll<DashboardSummary>(() => api.dashboardSummary(), 15_000);
  // Admin-only endpoint: don't poll it below admin (it would only 403).
  // Over-fetch, then drop routine device sign-in rows so they don't push operator actions out
  // of the 5 slots; the full log (with a 「顯示例行紀錄」 toggle) is one click away.
  const audit = usePoll<AuditEntry[]>(() => (isAdmin ? api.listAudit({ limit: 40 }) : Promise.resolve([])), 30_000, [isAdmin]);
  const recent = (audit.data ?? []).filter((a) => !isRoutineAudit(a)).slice(0, 5);

  const s = stats.data;
  const updatedAt = s?.generated_at ?? null;

  return (
    <div className="v2-page">
      <div className="v2-head">
        <div className="titles">
          <h1 className="v2-title">總覽</h1>
          <p className="v2-sub">名單人數、授權池與待處理事項 · 每 15 秒更新</p>
        </div>
        {updatedAt && <span className="muted-note">更新於 {hhmm(updatedAt)}</span>}
      </div>

      {!forbidden(stats.error) && (
        <>
          {Boolean(stats.error) && !s && <div className="error-line">名單統計載入失敗:{errText(stats.error)}</div>}
          <div className="tiles c5">
            <Kpi label="名單總數" value={s?.total} sub="含撤銷、拒絕、到期" />
            <Kpi label="有效" value={s?.by_status.active} sub="目前可使用" tone="tone-success" />
            <Kpi label="已撤銷" value={s?.by_status.revoked} sub="只能由後台復原" />
            <Kpi label="已拒絕" value={s?.by_status.rejected} sub="加入時即拒絕" />
            <Kpi label="已到期" value={s?.by_status.expired} sub="到期日已過" tone="tone-warning" />
          </div>
          <OriginPanel stats={s} linkable={isAdmin} />
        </>
      )}

      <div className="split-3-2">
        {!forbidden(lic.error) ? (
          <section className="panel">
            <div className="panel-head">
              <h2 className="panel-title grow">軟體序號</h2>
              <Link to="/insights" className="v2-back">依批次看 →</Link>
            </div>
            {Boolean(lic.error) && !lic.data && <div className="error-line">序號統計載入失敗:{errText(lic.error)}</div>}
            <div className="tiles c4" style={{ gap: 10 }}>
              <Kpi soft label="已兌換" value={lic.data?.activated} sub={lic.data ? `兌換率 ${pct(lic.data.activated, lic.data.minted)}%` : ' '} />
              <Kpi soft label="剩餘可兌換" value={lic.data?.unredeemed} sub={lic.data ? `共鑄造 ${lic.data.minted} 組` : ' '} />
              <Kpi soft label="已撤銷" value={lic.data?.revoked} sub="不可復原" />
              <Kpi soft label="今日兌換" value={lic.data?.today} sub={lic.data ? `7 日 ${lic.data.d7} · 30 日 ${lic.data.d30}` : ' '} />
            </div>
            {!forbidden(timeline.error) && (
              <>
                <div className="muted-note">近 30 日每日兌換</div>
                {timeline.data ? (
                  <SeriesChart
                    type="bar"
                    data={timeline.data.overall}
                    series={[{ key: 'activated', name: '每日兌換', color: 'var(--primary)' }]}
                    height={170}
                  />
                ) : (
                  <span className="muted-note">{timeline.error ? `載入失敗:${errText(timeline.error)}` : '載入中…'}</span>
                )}
              </>
            )}
          </section>
        ) : (
          <div />
        )}

        <div className="stack-14">
          <TodoPanel summary={summary.data} stats={s} role={role} />
          {isAdmin && !forbidden(audit.error) && (
            <section className="panel" style={{ gap: 'var(--space-2)' }}>
              <div className="panel-head">
                <h2 className="panel-title grow">最近活動</h2>
                <Link to="/audit" className="v2-back">全部 →</Link>
              </div>
              {recent.length === 0 ? (
                <span className="muted-note">{audit.loading && !audit.data ? '載入中…' : '尚無活動(不含例行的 App 連線紀錄)'}</span>
              ) : (
                recent.map((a) => (
                  <div key={a.id} className="activity" title={fmtDate(a.ts)}>
                    <span className="at">{fmtRelative(a.ts)}</span>
                    <span>
                      {a.actor_id ? <span title={auditActor(a).title}>{auditActor(a).text} · </span> : null}
                      {auditSentence(a)}
                    </span>
                  </div>
                ))
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, tone, soft }: { label: string; value: number | undefined; sub: string; tone?: string; soft?: boolean }) {
  return (
    <div className={'tile' + (soft ? ' soft' : '')}>
      <div className="tile-label">{label}</div>
      <div className={'tile-value ' + (tone ?? '')}>{value ?? '—'}</div>
      <div className="tile-sub">{sub}</div>
    </div>
  );
}

function OriginPanel({ stats, linkable }: { stats: RosterStats | null; linkable: boolean }) {
  const total = stats?.total ?? 0;
  const parts = ORIGIN_ORDER.map((o) => ({ o, n: stats?.by_origin[o] ?? 0 }));
  const aria = '名單來源:' + parts.map((p) => `${ORIGIN_LABEL[p.o]} ${p.n}`).join('、');
  return (
    <section className="panel">
      <div className="panel-head">
        <h2 className="panel-title grow">名單從哪裡來</h2>
        <span className="muted-note">有軟體序號綁到這台 = 序號兌換;否則看加入方式</span>
      </div>
      <div className="stackbar" role="img" aria-label={aria}>
        {total > 0 &&
          parts.filter((p) => p.n > 0).map((p) => (
            <span key={p.o} style={{ width: `${(p.n / total) * 100}%`, background: ORIGIN_COLOR[p.o] }} />
          ))}
      </div>
      <div className="legend-grid">
        {parts.map((p) => {
          const body = (
            <>
              <span className="k"><Swatch origin={p.o} />{ORIGIN_LABEL[p.o]}</span>
              <span className="v">{stats ? p.n : '—'}</span>
              <span className="s">
                {stats ? `${pct(p.n, total)}%` : ' '}
                {linkable && p.o === 'unknown' && p.n > 0 ? ' · 逐筆檢視 →' : ''}
              </span>
            </>
          );
          return linkable ? (
            <Link key={p.o} className="legend-item" to={`/roster?origin=${p.o}`}>{body}</Link>
          ) : (
            <div key={p.o} className="legend-item">{body}</div>
          );
        })}
      </div>
    </section>
  );
}

function TodoPanel({ summary, stats, role }: { summary: DashboardSummary | null; stats: RosterStats | null; role: import('@/types').Role | null }) {
  const items: { key: string; n: number; label: string; to: string | null; warn: boolean }[] = [];
  if (typeof summary?.open_tickets === 'number') {
    items.push({ key: 't', n: summary.open_tickets, label: '待處理工單', to: roleAtLeast(role, 'support') ? '/support' : null, warn: summary.open_tickets > 0 });
  }
  if (typeof summary?.pending_admins === 'number') {
    items.push({ key: 'a', n: summary.pending_admins, label: '待審核管理員', to: roleAtLeast(role, 'super_admin') ? '/users' : null, warn: summary.pending_admins > 0 });
  }
  if (stats) {
    items.push({ key: 'e', n: stats.expiring_7d, label: '7 天內到期的機器', to: roleAtLeast(role, 'admin') ? '/roster?status=expiring7' : null, warn: false });
  }
  if (items.length === 0) return null;
  return (
    <section className="panel" style={{ gap: 10 }}>
      <h2 className="panel-title">待處理</h2>
      {items.map((it) => {
        const inner = (
          <>
            <span className="n">{it.n}</span>
            <span className="grow">{it.label}</span>
            {it.to && <span className="hint">→</span>}
          </>
        );
        return it.to ? (
          <Link key={it.key} to={it.to} className={'todo' + (it.warn ? ' warn' : '')}>{inner}</Link>
        ) : (
          <div key={it.key} className={'todo' + (it.warn ? ' warn' : '')}>{inner}</div>
        );
      })}
    </section>
  );
}
